import { ConfirmModal } from '@/components/ConfirmModal';
import { StatusModal } from '@/components/StatusModal';
import { FormaSwitch } from '@/components/sklad/FormaSwitch';
import { LatkaPicker } from '@/components/sklad/LatkaPicker';
import { type PrintTarget, PrintTargetModal } from '@/components/sklad/PrintTargetModal';
import { RollDetailModal } from '@/components/sklad/RollDetailModal';
import { tiskStitkuRole, tiskneNaZebru, zapamatujCilTisku } from '@/components/sklad/printRoll';
import {
  type Latka,
  NO_TYPE,
  PALETTE,
  type Piece,
  SPENT_COLOR,
  SPENT_KEY,
  SPENT_LABEL,
  formatQty,
  kusLabel,
  matches,
  parseQty,
  tokenize,
} from '@/components/sklad/types';
import { useEscapeKey } from '@/hooks/useEscapeKey';
import { ApiError, apiFetch } from '@/lib/apiFetch';
import { useCallback, useEffect, useMemo, useState } from 'react';

type Category = { type: string; pieces: Piece[]; totalQty: number; unit: string };

const CARD = 'bg-surface rounded-xl shadow-card';

const ICON_PROPS = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.5,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
} as const;

function BackIcon() {
  return (
    <svg width="16" height="16" {...ICON_PROPS} aria-hidden="true">
      <line x1="19" y1="12" x2="5" y2="12" />
      <polyline points="12 19 5 12 12 5" />
    </svg>
  );
}

function HouseIcon() {
  return (
    <svg width="30" height="30" {...ICON_PROPS} aria-hidden="true">
      <path d="M3 9.5 12 3l9 6.5" />
      <path d="M5 10v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V10" />
      <path d="M9 21v-6h6v6" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg width="18" height="18" {...ICON_PROPS} aria-hidden="true">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function GearIcon() {
  return (
    <svg width="18" height="18" {...ICON_PROPS} aria-hidden="true">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="18" height="18" {...ICON_PROPS} aria-hidden="true">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  );
}

function PrinterIcon() {
  return (
    <svg width="18" height="18" {...ICON_PROPS} aria-hidden="true">
      <polyline points="6 9 6 2 18 2 18 9" />
      <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
      <rect x="6" y="14" width="12" height="8" />
    </svg>
  );
}

/**
 * Sklad látek — materiál fyzicky na dílně. Dvě úrovně: dlaždice druhů látek →
 * tabulka jednotlivých rolí → detail role (odečítání, historie).
 * Skladovou jednotkou je role, ne látka.
 */
export function SkladLatekPage() {
  const [pieces, setPieces] = useState<Piece[] | null>(null);
  const [colors, setColors] = useState<Record<string, string>>({});
  const [isAdmin, setIsAdmin] = useState(false);
  const [activeType, setActiveType] = useState<string | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [query, setQuery] = useState('');
  const [detailId, setDetailId] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Piece | null>(null);
  const [stockTarget, setStockTarget] = useState<Piece | null>(null);
  const [stockValue, setStockValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [printTarget, setPrintTarget] = useState<Piece | null>(null);
  const [toast, setToast] = useState<{ msg: string; kind: 'success' | 'error' } | null>(null);

  const load = useCallback(async () => {
    try {
      const [p, c] = await Promise.all([
        apiFetch<{ pieces: Piece[] }>('/api/sklad/pieces'),
        apiFetch<{ colors: Record<string, string> }>('/api/sklad/type-colors'),
      ]);
      setPieces(p.pieces);
      setColors(c.colors);
    } catch (e: unknown) {
      setPieces([]);
      setToast({ msg: e instanceof ApiError ? e.message : 'Chyba načítání', kind: 'error' });
    }
  }, []);

  /** Dotisk štítku role — nejdřív se zeptáme, kam se má tisknout. */
  const tisknoutStitek = useCallback((piece: Piece) => setPrintTarget(piece), []);

  async function vytiskni(piece: Piece, target: PrintTarget) {
    setPrintTarget(null);
    // PDF je jednorázová kontrola, nic si z ní nepamatujeme.
    if (target !== 'pdf') zapamatujCilTisku(target === 'fronta');
    try {
      await tiskStitkuRole(piece, target);
      if (target === 'fronta') {
        setToast({
          msg: `Štítek role č. ${piece.serial_number} odeslán na Zebru`,
          kind: 'success',
        });
      }
    } catch (e: unknown) {
      setToast({
        msg: `Štítek se nevytiskl: ${
          e instanceof ApiError ? e.describe() : e instanceof Error ? e.message : 'chyba'
        }`,
        kind: 'error',
      });
    }
  }

  useEffect(() => {
    load();
    apiFetch<{ role: string }>('/api/auth/me')
      .then((u) => setIsAdmin(u.role === 'admin'))
      .catch(() => setIsAdmin(false));
    // Fonty pro štítky natáhneme dopředu, ať tisk nečeká na síť.
    void import('@/components/sklad/labelsPdf').then((m) => m.prefetchLabelFonts());
  }, [load]);

  const all = useMemo(() => pieces ?? [], [pieces]);
  const tokens = useMemo(() => tokenize(query), [query]);

  // Kategorie = druhy látek odvozené z rolí reálně na skladě (zdělané vyňaty).
  const categories = useMemo<Category[]>(() => {
    const map = new Map<string, Category>();
    for (const p of all) {
      if (p.spent_at) continue;
      const key = p.latka_type || NO_TYPE;
      const entry = map.get(key) ?? { type: key, pieces: [], totalQty: 0, unit: p.unit };
      entry.pieces.push(p);
      entry.totalQty += p.stock_qty;
      map.set(key, entry);
    }
    return [...map.values()].sort((a, b) => a.type.localeCompare(b.type, 'cs'));
  }, [all]);

  const spentPieces = useMemo(() => all.filter((p) => p.spent_at), [all]);
  const filteredCategories = useMemo(
    () => categories.filter((c) => matches(c.type, tokens)),
    [categories, tokens],
  );
  const showSpentTile = spentPieces.length > 0 && matches(SPENT_LABEL, tokens);

  // Plochý výpis všech rolí na skladě (napříč druhy) — rychlé hledání konkrétní látky.
  const stockList = useMemo(
    () =>
      all
        .filter((p) => !p.spent_at)
        .filter((p) => matches(`${p.serial_number} ${p.latka_name} ${p.latka_type}`, tokens))
        .sort(
          (a, b) =>
            a.latka_name.localeCompare(b.latka_name, 'cs') || a.serial_number - b.serial_number,
        ),
    [all, tokens],
  );

  const activePieces = useMemo(() => {
    if (activeType === null) return [];
    const base =
      activeType === SPENT_KEY
        ? all.filter((p) => p.spent_at)
        : all.filter((p) => !p.spent_at && (p.latka_type || NO_TYPE) === activeType);
    return base.filter((p) =>
      matches(`${p.serial_number} ${p.latka_name} ${p.latka_type}`, tokens),
    );
  }, [all, activeType, tokens]);

  const activeCategory = useMemo<Category | null>(() => {
    if (activeType === SPENT_KEY) {
      return {
        type: SPENT_LABEL,
        pieces: spentPieces,
        totalQty: 0,
        unit: spentPieces[0]?.unit ?? 'm',
      };
    }
    return categories.find((c) => c.type === activeType) ?? null;
  }, [categories, activeType, spentPieces]);

  const detail = useMemo(() => all.find((p) => p.id === detailId) ?? null, [all, detailId]);
  const siblings = useMemo(
    () =>
      detail
        ? all
            .filter((p) => p.latka_id === detail.latka_id && p.id !== detail.id)
            .sort((a, b) => a.serial_number - b.serial_number)
        : [],
    [all, detail],
  );

  /** Obalí akci nad rolí: hlídá rozpracovanost, hlásí výsledek a přenačte data. */
  async function run(fn: () => Promise<void>, okMsg: string, errMsg: string) {
    setBusy(true);
    try {
      await fn();
      await load();
      setToast({ msg: okMsg, kind: 'success' });
    } catch (e: unknown) {
      setToast({ msg: e instanceof ApiError ? e.describe() : errMsg, kind: 'error' });
    } finally {
      setBusy(false);
    }
  }

  // Barva druhu — optimisticky, při chybě vrátíme původní.
  async function applyColor(type: string, value: string) {
    const prev = colors[type] ?? '';
    if (prev === value) return;
    setColors((c) => ({ ...c, [type]: value }));
    try {
      await apiFetch('/api/sklad/type-colors', {
        method: 'PUT',
        body: { type, color: value },
      });
    } catch (e: unknown) {
      setColors((c) => ({ ...c, [type]: prev }));
      setToast({ msg: e instanceof ApiError ? e.message : 'Chyba ukládání barvy', kind: 'error' });
    }
  }

  const inDetail = activeType !== null;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-text">Sklad látek</h1>
        <p className="text-sm text-text-secondary mt-1">
          Materiál fyzicky na dílně. Vyber druh látky a rozklikni jednotlivé role.
        </p>
      </div>

      {(inDetail || showSettings) && (
        <button
          type="button"
          onClick={() => {
            setActiveType(null);
            setShowSettings(false);
            setQuery('');
          }}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-primary-dark cursor-pointer mb-4"
        >
          <BackIcon />
          {showSettings ? 'Zpět na sklad' : 'Zpět na druhy látek'}
        </button>
      )}

      {inDetail && activeCategory && (
        <div
          className={`${CARD} mb-4 p-4 flex items-center gap-4 border border-border`}
          style={
            activeType === SPENT_KEY
              ? { backgroundColor: SPENT_COLOR }
              : activeType && colors[activeType]
                ? { backgroundColor: colors[activeType] }
                : undefined
          }
        >
          <button
            type="button"
            onClick={() => {
              setActiveType(null);
              setQuery('');
            }}
            title="Zpět na sklad látek"
            aria-label="Zpět na sklad látek"
            className="w-14 h-14 rounded-xl bg-white/60 border border-black/5 flex items-center justify-center shrink-0 text-text-secondary hover:bg-white/90 hover:text-primary transition-colors cursor-pointer"
          >
            <HouseIcon />
          </button>
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-text break-words">{activeCategory.type}</h2>
            <p className="text-sm text-text-secondary mt-0.5">
              {activeCategory.pieces.length} {kusLabel(activeCategory.pieces.length)}
              {activeType !== SPENT_KEY && (
                <> · celkem {formatQty(activeCategory.totalQty, activeCategory.unit)}</>
              )}
            </p>
          </div>
        </div>
      )}

      {showSettings ? (
        <div>
          <div className="mb-4">
            <h2 className="text-lg font-bold text-text">Barvy druhů látek</h2>
            <p className="text-sm text-text-secondary mt-0.5">
              Přiřaď druhům barvu, ať máš příbuzné materiály na první pohled pohromadě. Ukládá se
              hned.
            </p>
          </div>
          {categories.length === 0 ? (
            <div className={`${CARD} px-5 py-10 text-center text-text-muted`}>
              Zatím žádný druh látky.
            </div>
          ) : (
            <div className={`${CARD} divide-y divide-border-light`}>
              {categories.map((c) => (
                <div
                  key={c.type}
                  className="flex items-center justify-between gap-4 px-5 py-3.5 flex-wrap"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span
                      className="w-6 h-6 rounded border border-border shrink-0"
                      style={{ backgroundColor: colors[c.type] || '#ffffff' }}
                    />
                    <div className="min-w-0">
                      <div className="font-medium text-text truncate">{c.type}</div>
                      <div className="text-xs text-text-muted">
                        {c.pieces.length} {kusLabel(c.pieces.length)}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {PALETTE.map((sw) => (
                      <button
                        key={sw.name}
                        type="button"
                        onClick={() => applyColor(c.type, sw.value)}
                        title={sw.name}
                        aria-label={sw.name}
                        className={`w-7 h-7 rounded-full border transition-all cursor-pointer ${
                          (colors[c.type] || '') === sw.value
                            ? 'ring-2 ring-primary ring-offset-1 border-transparent'
                            : 'border-border hover:scale-110'
                        }`}
                        style={{ backgroundColor: sw.value || '#ffffff' }}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="flex items-center gap-3 mb-4">
            <div className="relative flex-1 max-w-md">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted">
                <SearchIcon />
              </span>
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={inDetail ? 'Hledat číslo, název…' : 'Hledat druh materiálu…'}
                className="w-full text-sm text-text bg-surface border border-border rounded-lg pl-10 pr-3.5 py-2.5 outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 transition-all"
              />
            </div>
            <button
              type="button"
              onClick={() => setAddOpen(true)}
              title="Přidat na sklad látku, která nepřišla objednávkou"
              className="inline-flex items-center gap-1 bg-action hover:bg-action-dark text-white text-base font-bold rounded-lg px-6 py-3 shadow-sm transition-colors cursor-pointer shrink-0"
            >
              + Nová látka
            </button>
            {!inDetail && (
              <button
                type="button"
                onClick={() => setShowSettings(true)}
                title="Nastavení barev druhů"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-text-secondary border border-border rounded-lg px-3.5 py-2.5 hover:bg-surface-hover transition-colors cursor-pointer shrink-0"
              >
                <GearIcon />
                Nastavení
              </button>
            )}
          </div>

          {!inDetail && (
            <>
              {pieces === null ? (
                <div className={`${CARD} px-5 py-10 text-center text-text-muted`}>Načítám…</div>
              ) : filteredCategories.length === 0 && !showSpentTile ? (
                <div className={`${CARD} px-5 py-10 text-center text-text-muted`}>
                  {categories.length === 0 && spentPieces.length === 0
                    ? 'Zatím žádný materiál. Po příjmu objednávky se tu objeví.'
                    : 'Žádný druh neodpovídá hledání.'}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                  {filteredCategories.map((c) => (
                    <button
                      key={c.type}
                      type="button"
                      onClick={() => {
                        setActiveType(c.type);
                        setQuery('');
                      }}
                      style={colors[c.type] ? { backgroundColor: colors[c.type] } : undefined}
                      className={`${CARD} text-left p-5 border border-border hover:shadow-card-hover transition-all cursor-pointer`}
                    >
                      <div className="font-semibold text-text truncate">{c.type}</div>
                      <div className="text-xs text-text-secondary mt-1">
                        {c.pieces.length} {kusLabel(c.pieces.length)} ·{' '}
                        {formatQty(c.totalQty, c.unit)}
                      </div>
                    </button>
                  ))}
                  {showSpentTile && (
                    <button
                      type="button"
                      onClick={() => {
                        setActiveType(SPENT_KEY);
                        setQuery('');
                      }}
                      style={{ backgroundColor: SPENT_COLOR }}
                      className="text-left rounded-xl shadow-card p-5 border border-danger/30 hover:shadow-card-hover transition-all cursor-pointer"
                    >
                      <div className="font-semibold text-danger truncate">{SPENT_LABEL}</div>
                      <div className="text-xs text-danger/80 mt-1">
                        {spentPieces.length} {kusLabel(spentPieces.length)}
                      </div>
                    </button>
                  )}
                </div>
              )}

              {stockList.length > 0 && (
                <div className="mt-8">
                  <h2 className="text-base font-semibold text-text mb-3">
                    Skladové látky{' '}
                    <span className="text-text-muted font-normal">({stockList.length})</span>
                  </h2>
                  <PiecesTable
                    pieces={stockList}
                    thumbSize={80}
                    onOpen={setDetailId}
                    onPrint={tisknoutStitek}
                  />
                </div>
              )}
            </>
          )}
        </>
      )}

      {inDetail && (
        <PiecesTable
          pieces={activePieces}
          thumbSize={40}
          onOpen={setDetailId}
          onPrint={tisknoutStitek}
          onDelete={isAdmin ? (p) => setDeleteTarget(p) : undefined}
          onEditStock={(p) => {
            setStockTarget(p);
            setStockValue(String(p.stock_qty));
          }}
        />
      )}

      {stockTarget && (
        <StockModal
          piece={stockTarget}
          value={stockValue}
          busy={busy}
          onChange={setStockValue}
          onClose={() => setStockTarget(null)}
          onSave={() => {
            const qty = parseQty(stockValue);
            if (!Number.isFinite(qty) || qty < 0) {
              setToast({ msg: 'Zadej platné množství', kind: 'error' });
              return;
            }
            const id = stockTarget.id;
            setStockTarget(null);
            run(
              async () => {
                await apiFetch(`/api/sklad/pieces/${id}/stock`, {
                  method: 'POST',
                  body: { quantity: Math.round(qty * 100) / 100 },
                });
              },
              'Stav upraven',
              'Chyba při ukládání',
            );
          }}
        />
      )}

      {detail && (
        <RollDetailModal
          piece={detail}
          siblings={siblings}
          busy={busy}
          onOpenSibling={(p) => setDetailId(p.id)}
          onPrint={() => tisknoutStitek(detail)}
          onClose={() => setDetailId(null)}
          onSetStock={(qty) =>
            run(
              async () => {
                await apiFetch(`/api/sklad/pieces/${detail.id}/stock`, {
                  method: 'POST',
                  body: { quantity: qty },
                });
              },
              'Stav upraven',
              'Chyba při ukládání',
            )
          }
          onSetFolded={(folded) =>
            run(
              async () => {
                await apiFetch(`/api/sklad/pieces/${detail.id}/folded`, {
                  method: 'POST',
                  body: { folded },
                });
              },
              folded ? 'Označeno jako složené' : 'Označeno jako role',
              'Chyba při ukládání',
            )
          }
          onSubtract={(amount) =>
            run(
              async () => {
                await apiFetch(`/api/sklad/pieces/${detail.id}/subtract`, {
                  method: 'POST',
                  body: { amount },
                });
              },
              `Odečteno ${amount} ${detail.unit}`,
              'Chyba při odečítání',
            )
          }
          onMarkSpent={() =>
            run(
              async () => {
                await apiFetch(`/api/sklad/pieces/${detail.id}/spent`, { method: 'POST' });
              },
              'Látka označena jako zdělaná',
              'Chyba při označení',
            )
          }
          onUnmarkSpent={() =>
            run(
              async () => {
                await apiFetch(`/api/sklad/pieces/${detail.id}/spent`, { method: 'DELETE' });
              },
              'Role vrácena na sklad',
              'Chyba při vracení',
            )
          }
        />
      )}

      {deleteTarget && (
        <ConfirmModal
          title="Smazat roli"
          message={
            <>
              Opravdu smazat roli <strong>č. {deleteTarget.serial_number}</strong> (
              {deleteTarget.latka_name}) ze skladu? Smaže se i její historie pohybů.
            </>
          }
          confirmText="Smazat"
          confirmKind="danger"
          onCancel={() => setDeleteTarget(null)}
          onConfirm={() => {
            const id = deleteTarget.id;
            setDeleteTarget(null);
            run(
              async () => {
                await apiFetch(`/api/sklad/pieces/${id}`, { method: 'DELETE' });
              },
              'Role smazána ze skladu',
              'Chyba při mazání',
            );
          }}
        />
      )}

      {addOpen && (
        <PridatRoliModal
          busy={busy}
          onClose={() => setAddOpen(false)}
          onSave={async ({ tisk, naZebru, ...data }) => {
            setBusy(true);
            try {
              const r = await apiFetch<{ serial_number: number }>('/api/sklad/pieces', {
                method: 'POST',
                body: data,
              });
              setAddOpen(false);
              // Role už na skladě je — případný nezdar tisku tedy hlásíme
              // zvlášť, ať to nevypadá, že se nepřidala.
              let stitek = '';
              if (tisk) {
                try {
                  const { generateLabelsPdf } = await import('@/components/sklad/labelsPdf');
                  await generateLabelsPdf(
                    [
                      {
                        serial_number: r.serial_number,
                        latka_name: data.latka_name,
                        latka_image: data.latka_image,
                        quantity: data.quantity,
                        unit: 'm',
                        folded: data.folded ? 1 : 0,
                      },
                    ],
                    data.latka_name,
                    naZebru ? 'fronta' : 'dialog',
                  );
                  stitek = naZebru ? ' · štítek odeslán na Zebru' : '';
                } catch (e: unknown) {
                  stitek = ` · štítek se nevytiskl: ${
                    e instanceof ApiError ? e.describe() : e instanceof Error ? e.message : 'chyba'
                  }`;
                }
              }
              setToast({
                msg: `Role č. ${r.serial_number} přidána na sklad${stitek}`,
                kind: 'success',
              });
              await load();
            } catch (e: unknown) {
              setToast({
                msg: e instanceof ApiError ? e.describe() : 'Přidání role selhalo',
                kind: 'error',
              });
            } finally {
              setBusy(false);
            }
          }}
        />
      )}

      {printTarget && (
        <PrintTargetModal
          title={`Role č. ${printTarget.serial_number} · ${printTarget.latka_name}`}
          vychoziFronta={tiskneNaZebru()}
          onPick={(target) => vytiskni(printTarget, target)}
          onClose={() => setPrintTarget(null)}
        />
      )}

      {toast && (
        <StatusModal message={toast.msg} kind={toast.kind} onClose={() => setToast(null)} />
      )}
    </div>
  );
}

/** Rychlá úprava stavu role z tabulky (bez otevírání celého detailu). */
/** Přidání role mimo objednávku — zbytky, nákup na místě, inventura. */
function PridatRoliModal({
  busy,
  onClose,
  onSave,
}: {
  busy: boolean;
  onClose: () => void;
  onSave: (data: {
    latka_id: string;
    latka_code: string;
    latka_name: string;
    latka_type: string;
    latka_image: string;
    quantity: number;
    folded: boolean;
    note: string;
    /** Vytisknout k roli rovnou štítek. */
    tisk: boolean;
    /** Poslat štítek na dílenskou Zebru místo tiskového dialogu. */
    naZebru: boolean;
  }) => void;
}) {
  useEscapeKey(onClose);
  const [latky, setLatky] = useState<Latka[]>([]);
  const [loading, setLoading] = useState(true);
  const [latka, setLatka] = useState<Latka | null>(null);
  const [metry, setMetry] = useState('');
  const [folded, setFolded] = useState(false);
  const [note, setNote] = useState('');
  const [tisk, setTisk] = useState(true);
  // Kam se tiskne, si držíme společně s příjmem — je to stejná tiskárna.
  const [naZebru, setNaZebru] = useState(() => localStorage.getItem('sklad-tisk-zebra') === '1');

  useEffect(() => {
    apiFetch<{ latky: Latka[] }>('/api/sklad/latky')
      .then((d) => setLatky(d.latky))
      .catch(() => setLatky([]))
      .finally(() => setLoading(false));
  }, []);

  const qty = parseQty(metry);
  const valid = !!latka && Number.isFinite(qty) && qty > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto">
      <button
        type="button"
        aria-label="Zavřít"
        onClick={onClose}
        className="fixed inset-0 bg-black/40 cursor-default"
      />
      <div className="relative w-full max-w-lg my-10 bg-surface rounded-2xl shadow-modal p-6 reveal">
        <h2 className="text-lg font-bold text-text">Nová látka na sklad</h2>
        <p className="text-sm text-text-secondary mt-1">
          Pro role, které nepřišly objednávkou — co už máme na dílně, zbytky, nákup na místě.
        </p>

        <div className="mt-4">
          <span className="block text-sm font-semibold text-text mb-1">Látka</span>
          <LatkaPicker value={latka} options={latky} loading={loading} onChange={setLatka} />
        </div>

        <div className="mt-4 flex flex-wrap items-end gap-4">
          <label className="block">
            <span className="block text-sm font-semibold text-text mb-1">Metráž</span>
            <input
              type="text"
              inputMode="decimal"
              value={metry}
              onChange={(e) => setMetry(e.target.value)}
              placeholder="např. 24,5"
              className="w-36 text-text bg-bg border border-border rounded-lg px-3 py-2 text-lg font-bold outline-none focus:border-primary"
            />
          </label>
          <div>
            <span className="block text-sm font-semibold text-text mb-1">Forma</span>
            <FormaSwitch value={folded} onChange={setFolded} />
          </div>
        </div>

        <label className="block mt-4">
          <span className="block text-sm font-semibold text-text mb-1">Poznámka</span>
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="odkud role je (nepovinné)"
            className="w-full text-sm text-text bg-bg border border-border rounded-lg px-3 py-2 outline-none focus:border-primary"
          />
        </label>

        <div className="mt-5 border-t border-border pt-4">
          <label className="inline-flex items-center gap-2 text-sm text-text cursor-pointer">
            <input type="checkbox" checked={tisk} onChange={(e) => setTisk(e.target.checked)} />
            Vytisknout štítek
          </label>
          {tisk && (
            <label className="mt-2 flex items-center gap-2 text-sm text-text-secondary cursor-pointer">
              <input
                type="checkbox"
                checked={naZebru}
                onChange={(e) => {
                  setNaZebru(e.target.checked);
                  localStorage.setItem('sklad-tisk-zebra', e.target.checked ? '1' : '0');
                }}
              />
              Tisknout na Zebru (jinak se otevře tiskový dialog)
            </label>
          )}
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="text-sm font-semibold text-text-secondary border border-border rounded-lg px-4 py-2.5 hover:bg-surface-hover cursor-pointer transition-colors"
          >
            Zrušit
          </button>
          <button
            type="button"
            disabled={!valid || busy}
            onClick={() =>
              latka &&
              onSave({
                latka_id: latka.id,
                latka_code: latka.code,
                latka_name: latka.name,
                latka_type: latka.type,
                latka_image: latka.image,
                quantity: Math.round(qty * 100) / 100,
                folded,
                note,
                tisk,
                naZebru,
              })
            }
            className="bg-action hover:bg-action-dark text-white text-sm font-bold rounded-lg px-5 py-2.5 disabled:opacity-50 cursor-pointer transition-colors"
          >
            {busy ? 'Přidávám…' : tisk ? 'Přidat a vytisknout' : 'Přidat na sklad'}
          </button>
        </div>
      </div>
    </div>
  );
}

function StockModal({
  piece,
  value,
  busy,
  onChange,
  onSave,
  onClose,
}: {
  piece: Piece;
  value: string;
  busy: boolean;
  onChange: (v: string) => void;
  onSave: () => void;
  onClose: () => void;
}) {
  useEscapeKey(onClose);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Zavřít"
        onClick={onClose}
        className="absolute inset-0 bg-black/30 backdrop-blur-sm cursor-default"
      />
      <div className="relative w-full max-w-sm bg-surface rounded-2xl shadow-modal p-6 reveal">
        <h2 className="text-lg font-bold text-text mb-1">Upravit stav na skladě</h2>
        <p className="text-sm text-text-secondary mb-4">
          Role <strong className="text-text">č. {piece.serial_number}</strong> · {piece.latka_name}
        </p>
        <label className="block">
          <span className="text-sm font-medium text-text-secondary block mb-1.5">
            Aktuální stav ({piece.unit})
          </span>
          <input
            // biome-ignore lint/a11y/noAutofocus: modal se otevírá kliknutím na stav, editace je jeho jediný účel
            autoFocus
            type="text"
            inputMode="decimal"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onSave();
            }}
            placeholder="Např. 42.5"
            className="w-full text-sm text-text bg-bg border border-border rounded-lg px-3.5 py-2.5 outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 transition-all"
          />
        </label>
        <div className="flex justify-end gap-3 mt-6">
          <button
            type="button"
            onClick={onClose}
            className="text-sm font-semibold text-text-secondary border border-border rounded-lg px-4 py-2.5 hover:bg-surface-hover transition-colors cursor-pointer"
          >
            Zrušit
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={busy}
            className="bg-action hover:bg-action-dark text-white text-sm font-semibold rounded-lg px-6 py-2.5 cursor-pointer transition-colors disabled:opacity-50"
          >
            {busy ? 'Ukládám…' : 'Uložit stav'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Tabulka rolí — používá se pro plochý výpis i pro obsah jednoho druhu. */
function PiecesTable({
  pieces,
  thumbSize,
  onOpen,
  onPrint,
  onDelete,
  onEditStock,
}: {
  pieces: Piece[];
  thumbSize: number;
  onOpen: (id: number) => void;
  onPrint?: (piece: Piece) => void;
  onDelete?: (piece: Piece) => void;
  onEditStock?: (piece: Piece) => void;
}) {
  const akce = !!onPrint || !!onDelete;
  return (
    <div className={`${CARD} overflow-x-auto`}>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-bg text-left text-xs font-semibold text-text-muted uppercase tracking-wider">
            <th className="px-4 py-3 w-28" />
            <th className="px-5 py-3 w-20">Číslo</th>
            <th className="px-5 py-3">Název</th>
            <th className="px-5 py-3">Druh</th>
            <th className="px-5 py-3">Forma</th>
            <th className="px-5 py-3 text-right">Stav</th>
            {akce && <th className="px-5 py-3 text-right">Akce</th>}
          </tr>
        </thead>
        <tbody>
          {pieces.length === 0 && (
            <tr>
              <td colSpan={akce ? 7 : 6} className="px-5 py-8 text-center text-text-muted">
                Nic neodpovídá hledání.
              </td>
            </tr>
          )}
          {pieces.map((p) => (
            // biome-ignore lint/a11y/useKeyWithClickEvents: řádek tabulky, klik otevírá detail (interní nástroj)
            <tr
              key={p.id}
              onClick={() => onOpen(p.id)}
              className="border-b border-border-light hover:bg-surface-hover transition-colors cursor-pointer"
            >
              <td className="px-4 py-2.5">
                {p.latka_image ? (
                  <img
                    src={p.latka_image}
                    alt=""
                    loading="lazy"
                    style={{ width: thumbSize, height: thumbSize }}
                    className="rounded object-cover border border-border bg-bg"
                  />
                ) : (
                  <div
                    style={{ width: thumbSize, height: thumbSize }}
                    className="rounded bg-bg border border-border"
                  />
                )}
              </td>
              <td className="px-5 py-3 text-text font-bold">{p.serial_number}</td>
              <td className="px-5 py-3 text-text-secondary">{p.latka_name}</td>
              <td className="px-5 py-3 text-text-muted">{p.latka_type || '—'}</td>
              <td className="px-5 py-3 text-text-secondary">{p.folded ? 'Složené' : 'Role'}</td>
              <td className="px-5 py-3 text-right">
                {onEditStock ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditStock(p);
                    }}
                    title="Upravit stav"
                    className="text-sm font-semibold text-primary bg-primary-50 hover:bg-primary-100 px-2.5 py-1 rounded cursor-pointer transition-colors"
                  >
                    {formatQty(p.stock_qty, p.unit)}
                  </button>
                ) : (
                  <span className="font-medium text-text">{formatQty(p.stock_qty, p.unit)}</span>
                )}
              </td>
              {akce && (
                <td className="px-5 py-3">
                  <div className="flex items-center justify-end gap-3">
                    {onPrint && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onPrint(p);
                        }}
                        title="Vytisknout štítek role"
                        className="inline-flex items-center gap-2 text-[15px] font-semibold text-text border border-border rounded-lg px-4 py-2.5 hover:bg-surface-hover transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <PrinterIcon />
                        Tisknout
                      </button>
                    )}
                    {onDelete && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(p);
                        }}
                        className="text-text-secondary hover:text-danger transition-colors cursor-pointer"
                        title="Smazat roli ze skladu"
                      >
                        <TrashIcon />
                      </button>
                    )}
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
